import React, { useState, useEffect, useRef } from 'react';
import {
  Building2,
  Plus,
  Search,
  Edit3,
  Trash2,
  Users,
  FolderOpen,
  HardDrive,
  Settings,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Filter,
  MoreVertical,
  BookOpen,
  Upload,
  FileText,
  Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from '../components/AccessDenied';
import { getTenantFromUrl } from '../utils/enhancedTenantUtils';
import toast from 'react-hot-toast';
import {
  getTenants,
  createTenant,
  getTenantPlans,
  activateTenant,
  updateTenant,
  getEnhancedTenants,
  getTenantDetails,
  updateTenantStatus,
  getTenantDocuments,
  uploadTenantDocument,
  deleteTenantDocument,
  toggleTenantDocument,
  type TenantData,
  type CreateTenantRequest,
  type PlanConfig,
  type EnhancedTenantData,
  type EnhancedTenantsResponse,
  type TenantDocumentData
} from '../api';

type CreateTenantForm = CreateTenantRequest;

const TenantManagement: React.FC = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<EnhancedTenantData[]>([]);
  const [plans, setPlans] = useState<Record<string, PlanConfig>>({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedTenant, setSelectedTenant] = useState<EnhancedTenantData | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const [createForm, setCreateForm] = useState<CreateTenantForm>({
    subdomain: '',
    name: '',
    domain: '',
    subscription_tier: 'basic',
    contact_email: '',
    billing_email: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdTenant, setCreatedTenant] = useState<TenantData | null>(null);

  // Standards Library state
  const [detailsTab, setDetailsTab] = useState<'overview' | 'standards'>('overview');
  const [tenantDocs, setTenantDocs] = useState<TenantDocumentData[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Security check - only SUPER_ADMIN users on main domain can access
  if (!user || user.role !== 'SUPER_ADMIN') {
    return <AccessDenied requiredRole="SUPER_ADMIN" message="Tenant management is restricted to Super Admin users only." />;
  }

  // Removed domain check - SUPER_ADMIN can access from any domain
  // const tenantInfo = getTenantFromUrl();
  // if (!tenantInfo.isMainDomain) {
  //   return <AccessDenied requiredRole="SUPER_ADMIN" message="Tenant management is only available from the main domain." />;
  // }

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await getEnhancedTenants({
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter })
      });
      
      setTenants(response.tenants);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await getTenantPlans();
      setPlans(response.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchPlans();
  }, [pagination.page, searchTerm, statusFilter]);

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setErrors({});

    try {
      const response = await createTenant(createForm);
      
      // Store created tenant data and show success modal
      setCreatedTenant(response.tenant);
      setShowSuccessModal(true);
      
      // Close create modal and reset form
      setShowCreateModal(false);
      setCreateForm({
        subdomain: '',
        name: '',
        domain: '',
        subscription_tier: 'basic',
        contact_email: '',
        billing_email: ''
      });
      
      // Refresh the list
      fetchTenants();
    } catch (error: any) {
      if (error.response?.data?.details) {
        const fieldErrors: Record<string, string> = {};
        error.response.data.details.forEach((detail: string) => {
          if (detail.includes('subdomain')) fieldErrors.subdomain = detail;
          if (detail.includes('name')) fieldErrors.name = detail;
          if (detail.includes('email')) fieldErrors.contact_email = detail;
        });
        setErrors(fieldErrors);
      } else {
        const errorMessage = error.response?.data?.error || 'Failed to create tenant';
        setErrors({ general: errorMessage });
        toast.error(errorMessage);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (tenantId: number, newStatus: string) => {
    try {
      await updateTenantStatus(tenantId, newStatus);
      toast.success(`Tenant status updated to ${newStatus}`);
      fetchTenants();
    } catch (error: any) {
      console.error('Error updating tenant status:', error);
      toast.error(error.response?.data?.error || 'Failed to update tenant status');
    }
  };

  const handleTenantDetails = async (tenant: EnhancedTenantData) => {
    try {
      const response = await getTenantDetails(tenant.id);
      setSelectedTenant(response.tenant);
      setDetailsTab('overview');
      setTenantDocs([]);
      setShowDetailsModal(true);
    } catch (error: any) {
      console.error('Error fetching tenant details:', error);
      toast.error('Failed to fetch tenant details');
    }
  };

  const handleTabChange = async (tab: 'overview' | 'standards') => {
    setDetailsTab(tab);
    if (tab === 'standards' && selectedTenant && tenantDocs.length === 0) {
      setDocsLoading(true);
      try {
        const res = await getTenantDocuments(selectedTenant.id);
        setTenantDocs(res.documents);
      } catch {
        toast.error('Failed to load standards documents');
      } finally {
        setDocsLoading(false);
      }
    }
  };

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedTenant || !e.target.files?.length) return;
    const files = Array.from(e.target.files);
    setUploading(true);
    let successCount = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('document', file);
        const res = await uploadTenantDocument(selectedTenant.id, formData);
        setTenantDocs(prev => [res.document, ...prev]);
        successCount++;
      } catch (error: any) {
        toast.error(`${file.name}: ${error.response?.data?.error || 'Upload failed'}`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully`);
      pollDocStatus(selectedTenant.id);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const [deletingDocId, setDeletingDocId] = useState<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pollDocStatus = (tenantId: number) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(async () => {
      try {
        const res = await getTenantDocuments(tenantId);
        setTenantDocs(res.documents);
        const stillPending = res.documents.some(d => d.processing_status === 'pending' || d.processing_status === 'processing');
        if (stillPending) pollDocStatus(tenantId);
      } catch {}
    }, 2000);
  };

  const handleDocDelete = async (docId: number) => {
    if (!selectedTenant) return;
    setDeletingDocId(docId);
    try {
      await deleteTenantDocument(selectedTenant.id, docId);
      setTenantDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch {
      toast.error('Failed to delete document');
    } finally {
      setDeletingDocId(null);
    }
  };

  const [togglingDocId, setTogglingDocId] = useState<number | null>(null);

  const handleDocToggle = async (docId: number, currentlyEnabled: boolean) => {
    if (!selectedTenant) return;
    const nextEnabled = !currentlyEnabled;
    setTogglingDocId(docId);
    try {
      await toggleTenantDocument(selectedTenant.id, docId, nextEnabled);
      setTenantDocs(prev => prev.map(d => d.id === docId ? { ...d, is_enabled: nextEnabled } : d));
      toast.success(nextEnabled ? 'Standard enabled for SOP generation' : 'Standard disabled for SOP generation');
    } catch {
      toast.error('Failed to update document');
    } finally {
      setTogglingDocId(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'trial': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'inactive': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'suspended': return <XCircle className="w-4 h-4 text-red-700" />;
      default: return <XCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'trial': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'suspended': return 'bg-red-200 text-red-900';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'basic': return 'bg-blue-100 text-blue-800';
      case 'professional': return 'bg-purple-100 text-purple-800';
      case 'enterprise': return 'bg-gold-100 text-gold-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = !searchTerm || 
      tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.subdomain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || tenant.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="bg-gray-50">
      <div>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Building2 className="w-8 h-8 mr-3 text-electric-blue" />
              Tenant Management
            </h1>
            <p className="text-gray-600 mt-1">Manage tenant organizations and subscriptions</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-electric-blue text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Tenant
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Building2 className="w-8 h-8 text-electric-blue" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Tenants</p>
                <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">
                  {tenants.filter(t => t.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Trial</p>
                <p className="text-2xl font-bold text-gray-900">
                  {tenants.filter(t => t.status === 'trial').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <XCircle className="w-8 h-8 text-red-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-gray-900">
                  {tenants.filter(t => t.status === 'inactive').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search tenants by name, subdomain, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tenants Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-blue"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredTenants.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No tenants found
                    </td>
                  </tr>
                ) : (
                  filteredTenants.map((tenant) => (
                    <tr key={tenant.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                          <div className="text-sm text-gray-500">@{tenant.subdomain}</div>
                          <div className="text-sm text-gray-500">{tenant.contact_email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(tenant.status)}
                          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(tenant.status)}`}>
                            {tenant.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTierColor(tenant.subscription_tier)}`}>
                          {tenant.subscription_tier}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1 text-gray-400" />
                            {tenant.analytics?.totalUsers || 0} users ({tenant.analytics?.activeUsers || 0} active)
                          </div>
                          <div className="flex items-center">
                            <FolderOpen className="w-4 h-4 mr-1 text-gray-400" />
                            {tenant.analytics?.totalProjects || 0} projects
                          </div>
                          <div className="flex items-center">
                            <HardDrive className="w-4 h-4 mr-1 text-gray-400" />
                            {((tenant.analytics?.storageUsed || 0) / (1024 * 1024 * 1024)).toFixed(2)} GB
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(tenant.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleTenantDetails(tenant)}
                            className="text-electric-blue hover:text-blue-800"
                            title="View tenant details"
                          >
                            <Settings className="w-5 h-5" />
                          </button>
                          {tenant.status === 'inactive' ? (
                            <button
                              onClick={() => handleStatusChange(tenant.id, 'active')}
                              className="text-green-600 hover:text-green-900"
                              title="Activate tenant"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleStatusChange(tenant.id, 'inactive')}
                              className="text-red-600 hover:text-red-900"
                              title="Deactivate tenant"
                            >
                              <XCircle className="w-5 h-5" />
                            </button>
                          )}
                          <button className="text-electric-blue hover:text-blue-800" title="Edit tenant">
                            <Edit3 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                    {' '}to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium">{pagination.total}</span>
                    {' '}results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Tenant</h2>
              
              {errors.general && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {errors.general}
                </div>
              )}

              <form onSubmit={handleCreateTenant} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subdomain *
                  </label>
                  <input
                    type="text"
                    value={createForm.subdomain}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, subdomain: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-electric-blue focus:border-transparent ${
                      errors.subdomain ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="example-org"
                    required
                  />
                  {errors.subdomain && <p className="text-red-500 text-sm mt-1">{errors.subdomain}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-electric-blue focus:border-transparent ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Example Organization"
                    required
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    value={createForm.contact_email}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, contact_email: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-electric-blue focus:border-transparent ${
                      errors.contact_email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="admin@example.com"
                    required
                  />
                  {errors.contact_email && <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Email
                  </label>
                  <input
                    type="email"
                    value={createForm.billing_email}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, billing_email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                    placeholder="billing@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Tier
                  </label>
                  <select
                    value={createForm.subscription_tier}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, subscription_tier: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                  >
                    <option value="basic">Basic</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Domain (Optional)
                  </label>
                  <input
                    type="text"
                    value={createForm.domain}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, domain: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                    placeholder="app.example.com"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-4 py-2 bg-electric-blue text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                  >
                    {creating ? 'Creating...' : 'Create Tenant'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && createdTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              {/* Success Icon */}
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              
              {/* Success Message */}
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Tenant Created Successfully!</h2>
                <p className="text-gray-600 mb-4">
                  Your new tenant has been created and is ready to use.
                </p>
              </div>

              {/* Tenant Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Tenant Details:</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium">{createdTenant.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subdomain:</span>
                    <span className="font-medium">@{createdTenant.subdomain}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subscription:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getTierColor(createdTenant.subscription_tier)}`}>
                      {createdTenant.subscription_tier}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <div className="flex items-center">
                      {getStatusIcon(createdTenant.status)}
                      <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getStatusColor(createdTenant.status)}`}>
                        {createdTenant.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contact Email:</span>
                    <span className="font-medium">{createdTenant.contact_email}</span>
                  </div>
                </div>
              </div>

              {/* Access Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-900 mb-2">Access Information:</h4>
                <p className="text-blue-800 text-sm mb-2">
                  This tenant will be accessible at:
                </p>
                <div className="bg-white border border-blue-300 rounded px-3 py-2">
                  <code className="text-blue-600 text-sm">
                    https://{createdTenant.subdomain}.{window.location.hostname}
                  </code>
                </div>
                <p className="text-blue-700 text-xs mt-2">
                  For development, users can access tenant-specific features by setting the tenant context.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setCreatedTenant(null);
                  }}
                  className="px-4 py-2 bg-electric-blue text-white rounded-md hover:bg-blue-600"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Details Modal */}
      {showDetailsModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <Building2 className="w-6 h-6 mr-2 text-electric-blue" />
                    {selectedTenant.name}
                  </h2>
                  <p className="text-gray-600">@{selectedTenant.subdomain} • {selectedTenant.contact_email}</p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => handleTabChange('overview')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    detailsTab === 'overview'
                      ? 'border-electric-blue text-electric-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Users className="w-4 h-4 inline mr-1" />
                  Overview
                </button>
                <button
                  onClick={() => handleTabChange('standards')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ml-2 ${
                    detailsTab === 'standards'
                      ? 'border-electric-blue text-electric-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <BookOpen className="w-4 h-4 inline mr-1" />
                  Standards Library
                </button>
              </div>

              {detailsTab === 'standards' ? (
                <div>
                  {/* Upload area */}
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Standards & Reference Documents</h3>
                      <p className="text-sm text-gray-500">Documents uploaded here are automatically included as context in all SOP generation for this tenant.</p>
                    </div>
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.docx,.xlsx,.doc,.csv"
                        multiple
                        onChange={handleDocUpload}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center px-4 py-2 bg-electric-blue text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                      >
                        {uploading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploading ? 'Uploading...' : 'Upload Document'}
                      </button>
                    </div>
                  </div>

                  {docsLoading ? (
                    <div className="flex items-center justify-center py-12 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Loading documents...
                    </div>
                  ) : (() => {
                    const completedDocs = tenantDocs.filter(d => d.processing_status === 'completed');
                    const processingCount = tenantDocs.filter(d => d.processing_status === 'pending' || d.processing_status === 'processing').length;
                    return completedDocs.length === 0 && processingCount === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">No standards documents uploaded yet.</p>
                        <p className="text-xs text-gray-400 mt-1">Upload PDF, DOCX, or XLSX files to get started.</p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Included in SOPs</th>
                              <th className="px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {completedDocs.map(doc => {
                              const isEnabled = doc.is_enabled !== false;
                              return (
                              <tr key={doc.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div className="flex items-center">
                                    <FileText className={`w-4 h-4 mr-2 flex-shrink-0 ${isEnabled ? 'text-gray-400' : 'text-gray-300'}`} />
                                    <span className={`text-sm ${isEnabled ? 'text-gray-900' : 'text-gray-400'}`}>{doc.original_name}</span>
                                  </div>
                                  {doc.description && (
                                    <p className="text-xs text-gray-500 mt-0.5 ml-6">{doc.description}</p>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                                  {formatFileSize(doc.file_size)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                                  {new Date(doc.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <button
                                    onClick={() => handleDocToggle(doc.id, isEnabled)}
                                    disabled={togglingDocId === doc.id}
                                    role="switch"
                                    aria-checked={isEnabled}
                                    title={isEnabled ? 'Enabled — included in SOP generation for this tenant. Click to disable.' : 'Disabled — excluded from SOP generation for this tenant. Click to enable.'}
                                    className={`relative w-9 h-5 rounded-full transition-colors disabled:opacity-50 ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                                  >
                                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isEnabled ? 'translate-x-4' : ''}`} />
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => handleDocDelete(doc.id)}
                                    disabled={deletingDocId === doc.id}
                                    className="text-red-400 hover:text-red-600 disabled:opacity-50"
                                    title="Delete"
                                  >
                                    {deletingDocId === doc.id
                                      ? <Loader2 className="w-4 h-4 animate-spin" />
                                      : <Trash2 className="w-4 h-4" />}
                                  </button>
                                </td>
                              </tr>
                              );
                            })}
                            {processingCount > 0 && (
                              <tr>
                                <td colSpan={5} className="px-4 py-3">
                                  <div className="flex items-center gap-2 text-sm text-gray-400">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Processing {processingCount} file{processingCount > 1 ? 's' : ''}…
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <>
              {/* Status and Subscription */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    {getStatusIcon(selectedTenant.status)}
                    <span className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTenant.status)}`}>
                      {selectedTenant.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Current Status</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTierColor(selectedTenant.subscription_tier)}`}>
                    {selectedTenant.subscription_tier}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">Subscription Plan</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-lg font-semibold text-gray-900">
                    ${selectedTenant.analytics?.subscriptionInfo?.amount || 0}/month
                  </p>
                  <p className="text-xs text-gray-500">Billing Amount</p>
                </div>
              </div>

              {/* Analytics Overview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Analytics Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <Users className="w-8 h-8 text-blue-600" />
                      <div className="ml-3">
                        <p className="text-2xl font-bold text-gray-900">{selectedTenant.analytics?.totalUsers || 0}</p>
                        <p className="text-sm text-gray-600">Total Users</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                      <div className="ml-3">
                        <p className="text-2xl font-bold text-gray-900">{selectedTenant.analytics?.activeUsers || 0}</p>
                        <p className="text-sm text-gray-600">Active Users</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <FolderOpen className="w-8 h-8 text-purple-600" />
                      <div className="ml-3">
                        <p className="text-2xl font-bold text-gray-900">{selectedTenant.analytics?.totalProjects || 0}</p>
                        <p className="text-sm text-gray-600">Projects</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <HardDrive className="w-8 h-8 text-orange-600" />
                      <div className="ml-3">
                        <p className="text-2xl font-bold text-gray-900">
                          {((selectedTenant.analytics?.storageUsed || 0) / (1024 * 1024 * 1024)).toFixed(1)}GB
                        </p>
                        <p className="text-sm text-gray-600">Storage Used</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Users Management */}
              {selectedTenant.users && selectedTenant.users.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Users ({selectedTenant.users.length})</h3>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              User
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Role
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Last Login
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Projects
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedTenant.users.map((user) => (
                            <tr key={user.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  user.role === 'MASTER' ? 'bg-purple-100 text-purple-800' :
                                  user.role === 'ADMIN' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.role}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {user.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {user.projectCount || 0}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleStatusChange(selectedTenant.id, selectedTenant.status === 'active' ? 'inactive' : 'active')}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      selectedTenant.status === 'active'
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {selectedTenant.status === 'active' ? 'Deactivate' : 'Activate'} Tenant
                  </button>
                  <button
                    onClick={() => handleStatusChange(selectedTenant.id, 'suspended')}
                    disabled={selectedTenant.status === 'suspended'}
                    className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-md text-sm font-medium hover:bg-yellow-200 disabled:opacity-50"
                  >
                    Suspend Tenant
                  </button>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;